import { BaseProvider } from "./base.provider";

export interface AvatarUploadResponse {
  upload_url: string;
  form_fields: Record<string, string>;
}

export interface Vehicle {
  vehicle_id: string;
  user_id: string;
  username: string;
  make: string;
  model: string;
  year: number;
  vehicle_type: string;
  nickname: string | null;
  mod_list: string | null;
  photo_url: string | null;
  horsepower: number | null;
  drive: string | null;
  transmission: string | null;
  engine_position: string | null;
  created_at: string;
  updated_at: string;
}

export interface User {
  user_id: string;
  email?: string;
  username: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  active_card_id: string | null;
  active_card_url?: string | null;
  active_card_primary_color: string | null;
  active_card_secondary_color: string | null;
  active_card_font: string | null;
  active_card_embed_content: boolean | null;
  xp: number;
  created_at: string | null;
  updated_at?: string | null;
  date_of_birth: string | null;
  locale?: string;
  timezone?: string;
  check_in_count: number;
  is_active?: boolean;
  is_deleted?: boolean;
  credits?: number;
  referral_key?: string;
  referred_by?: string | null;
  vehicles?: Vehicle[];
}

export interface AdminUsersResponse {
  data: User[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface NearbyUser {
  user_id: string;
  username: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  xp: number;
  distance_km: number;
  score: number;
}

export interface RecommendedUsersParams {
  page?: number;
  lat?: number;
  lng?: number;
  w_xp?: number;
  w_in_cell?: number;
  w_has_profile_image?: number;
  w_has_vehicle?: number;
  w_followers?: number;
}

export interface PaginatedNearbyUsers {
  data: NearbyUser[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface LocationUpdate {
  user_id: string;
  username: string;
  current_club_id?: string;
  current_club_name?: string;
  connected_server_id?: string;
  lat: number;
  lng: number;
  speed_kmph: number;
  heading: number;
}

export interface PaginatedLocationUpdates {
  data: LocationUpdate[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface LiveLocationsParams {
  lat: number;
  lng: number;
  radius_meters: number;
  page?: number;
}

// A snapshot of a moving population: users expire and move between requests,
// so walking the pages can repeat or skip a user. Enough pages to fill a map,
// not a stable listing.
const LIVE_LOCATIONS_MAX_PAGES = 25;

class UsersProvider extends BaseProvider {
  getRecommended(params: RecommendedUsersParams = {}): Promise<PaginatedNearbyUsers> {
    const p = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) p.set(key, String(value));
    }
    return this.request<PaginatedNearbyUsers>(`/v1/users/recommended?${p}`);
  }

  liveLocations({
    lat,
    lng,
    radius_meters,
    page = 1,
  }: LiveLocationsParams): Promise<PaginatedLocationUpdates> {
    const p = new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
      radius_meters: String(radius_meters),
      page: String(page),
    });
    return this.request<PaginatedLocationUpdates>(
      `/v1/admin/users/live-locations?${p}`,
    );
  }

  // The map wants every user in the radius, and the endpoint pages at 200.
  async allLiveLocations(
    params: Omit<LiveLocationsParams, "page">,
  ): Promise<LocationUpdate[]> {
    const all: LocationUpdate[] = [];
    const seen = new Set<string>();
    for (let page = 1; page <= LIVE_LOCATIONS_MAX_PAGES; page++) {
      const res = await this.liveLocations({ ...params, page });
      for (const u of res.data ?? []) {
        if (seen.has(u.user_id)) continue;
        seen.add(u.user_id);
        all.push(u);
      }
      if (!res.has_next) break;
    }
    return all;
  }

  list(page: number = 1): Promise<AdminUsersResponse> {
    return this.request<AdminUsersResponse>(`/v1/admin/users?page=${page}`);
  }

  search(q: string, page: number = 1): Promise<User[]> {
    return this.request<User[]>(`/v1/users/search?q=${encodeURIComponent(q)}&page=${page}`);
  }

  getById(userId: string): Promise<User> {
    return this.request<User>(`/v1/users/${encodeURIComponent(userId)}`);
  }

  getBlocks(userId: string): Promise<string[]> {
    return this.request<string[]>(`/v1/admin/users/${encodeURIComponent(userId)}/blocks`);
  }

  getAvatarUploadUrl(userId: string): Promise<AvatarUploadResponse> {
    return this.request<AvatarUploadResponse>(
      `/v1/admin/users/${encodeURIComponent(userId)}/profile-picture`,
      { method: "POST" }
    );
  }

  async uploadAvatarToStorage(
    { upload_url, form_fields }: AvatarUploadResponse,
    file: File
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", file.type);
    form.append("file", file);
    const res = await fetch(upload_url, { method: "POST", body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Avatar upload failed: HTTP ${res.status}`);
    }
  }
}

export const usersProvider = new UsersProvider();
